import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Heart, Gift, Users, Send, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatEUR = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);
const formatDate = (s) => { if (!s) return ''; try { return new Date(s).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return s; } };

const PublicGiftList = () => {
  const { slug } = useParams();
  const [gl, setGl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ guest_name: '', guest_email: '', guest_phone: '', declared_amount: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [thanked, setThanked] = useState(false);

  useEffect(() => { fetchGl(); }, [slug]);

  const fetchGl = async () => {
    try {
      const r = await axios.get(`${API}/public/gift-lists/${slug}`);
      setGl(r.data);
    } catch (e) {
      toast.error('Lista non trovata');
    } finally { setLoading(false); }
  };

  const submit = async () => {
    if (!form.guest_name.trim() || !form.declared_amount || parseFloat(form.declared_amount) <= 0) {
      toast.error('Nome e importo sono obbligatori');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/gift-lists/${slug}/contribute`, {
        ...form,
        declared_amount: parseFloat(form.declared_amount),
      });
      setThanked(true);
      fetchGl();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore invio');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pink-600"></div></div>;
  if (!gl) return <div className="min-h-screen flex items-center justify-center"><Card><CardHeader><CardTitle>Lista non trovata</CardTitle><CardDescription>La lista potrebbe essere stata disattivata.</CardDescription></CardHeader></Card></div>;

  const isWedding = gl.type === 'wedding';
  const Icon = isWedding ? Heart : Gift;
  const gradient = isWedding ? 'from-pink-500 to-rose-500' : 'from-indigo-500 to-purple-500';
  const percent = gl.goal_amount ? Math.min(100, (gl.confirmed_total / gl.goal_amount) * 100) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-pink-50" data-testid="public-gift-page">
      {/* Hero */}
      <div className={`bg-gradient-to-r ${gradient} text-white`}>
        {gl.cover_image_url && (
          <div className="w-full h-48 sm:h-64 overflow-hidden">
            <img src={gl.cover_image_url} alt={gl.title} className="w-full h-full object-cover opacity-80" />
          </div>
        )}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 text-center">
          <div className="flex justify-center mb-3"><Icon className="w-12 h-12" /></div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">{gl.title}</h1>
          <p className="text-lg opacity-90">{gl.client_name}</p>
          {gl.event_date && <p className="text-sm opacity-80 mt-2">{formatDate(gl.event_date)}</p>}
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {gl.description && (
          <Card>
            <CardContent className="pt-6">
              <p className="text-slate-700 whitespace-pre-wrap text-center italic">{gl.description}</p>
            </CardContent>
          </Card>
        )}

        {/* Progress */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-pink-600" /> Andamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between mb-3">
              <div>
                <div className="text-3xl font-bold text-emerald-700">{formatEUR(gl.confirmed_total)}</div>
                {gl.goal_amount && <div className="text-xs text-slate-500">su {formatEUR(gl.goal_amount)}</div>}
              </div>
              <Badge variant="secondary" className="text-sm">{gl.contributors_count} amici hanno partecipato</Badge>
            </div>
            {percent !== null && (
              <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                <div className={`h-full bg-gradient-to-r ${gradient} transition-all`} style={{ width: `${percent}%` }}></div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Wishes */}
        {gl.recent_wishes?.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-lg">Auguri dagli amici</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {gl.recent_wishes.map((w, i) => (
                  <div key={i} className="border-l-4 border-pink-300 bg-pink-50/50 px-3 py-2 rounded">
                    <div className="text-sm text-slate-700 italic">&ldquo;{w.message}&rdquo;</div>
                    <div className="text-xs text-slate-500 mt-1">— {w.guest_name}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contribution form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <PartyPopper className="w-5 h-5 text-amber-500" />
              Partecipa al regalo
            </CardTitle>
            <CardDescription>Compila il modulo. Ti invieremo istruzioni per il bonifico.</CardDescription>
          </CardHeader>
          <CardContent>
            {thanked ? (
              <div className="text-center py-8 space-y-3" data-testid="thank-you">
                <PartyPopper className="w-16 h-16 mx-auto text-emerald-500" />
                <h3 className="text-2xl font-bold text-slate-800">Grazie di cuore! 💕</h3>
                <p className="text-slate-600 max-w-md mx-auto">
                  La tua adesione è stata registrata. Riceverai a breve le istruzioni per il bonifico.
                  Il tuo regalo sarà visibile solo dopo aver ricevuto il bonifico.
                </p>
                <Button variant="outline" onClick={() => { setThanked(false); setForm({ guest_name:'', guest_email:'', guest_phone:'', declared_amount:'', message:'' }); }}>
                  Registra un altro contributo
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Il tuo nome *</Label>
                    <Input value={form.guest_name} onChange={(e) => setForm({...form, guest_name: e.target.value})} data-testid="guest-name" />
                  </div>
                  <div>
                    <Label>Importo (€) *</Label>
                    <Input type="number" step="0.01" min="1" value={form.declared_amount} onChange={(e) => setForm({...form, declared_amount: e.target.value})} data-testid="guest-amount" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={form.guest_email} onChange={(e) => setForm({...form, guest_email: e.target.value})} />
                  </div>
                  <div>
                    <Label>Telefono</Label>
                    <Input value={form.guest_phone} onChange={(e) => setForm({...form, guest_phone: e.target.value})} />
                  </div>
                </div>
                <div>
                  <Label>Messaggio di auguri</Label>
                  <Textarea rows={3} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} placeholder="Tanti auguri! Vi voglio bene..." />
                </div>
                <Button onClick={submit} disabled={submitting} className={`w-full bg-gradient-to-r ${gradient} hover:opacity-90`} data-testid="submit-contribution">
                  <Send className="w-4 h-4 mr-1" />
                  {submitting ? 'Invio in corso...' : 'Partecipa al regalo'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PublicGiftList;
